(function (root, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }
  root.CCBatchCalculatorCore = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  var PHASES = ['prebatch', 'add_at_service', 'garnish', 'manual'];
  var PHASE_LABELS = {
    prebatch: 'Combine in the pre-service batch',
    add_at_service: 'Measure at service',
    garnish: 'Garnish',
    manual: 'Keep manual'
  };
  var DILUTION_MODES = ['service', 'measured', 'base_percent', 'finished_percent'];
  var ML_PER_US_FLOZ = 29.5735295625;
  var EPSILON_ML = 1e-9;
  var MAX_COMPONENT_ML = 2000;
  var MAX_RESULT_ML = 100000000;

  function isFiniteNumber(value) {
    return typeof value === 'number' && Number.isFinite(value);
  }

  function cleanText(value, maximumLength) {
    return typeof value === 'string' ? value.trim().slice(0, maximumLength) : '';
  }

  function numberValue(value) {
    if (typeof value === 'string' && value.trim() === '') {
      return NaN;
    }
    return Number(value);
  }

  function roundTo(value, places) {
    var multiplier = Math.pow(10, places);
    return Math.round((value + Number.EPSILON) * multiplier) / multiplier;
  }

  function ozToMl(ounces, mlPerUsFloz) {
    if (!isFiniteNumber(ounces) || ounces < 0) {
      throw new TypeError('Ounces must be a finite, non-negative number.');
    }
    if (!isFiniteNumber(mlPerUsFloz) || mlPerUsFloz <= 0) {
      throw new TypeError('The fluid-ounce conversion must be positive.');
    }
    return ounces * mlPerUsFloz;
  }

  function mlToOz(milliliters, mlPerUsFloz) {
    if (!isFiniteNumber(milliliters) || milliliters < 0) {
      throw new TypeError('Milliliters must be a finite, non-negative number.');
    }
    if (!isFiniteNumber(mlPerUsFloz) || mlPerUsFloz <= 0) {
      throw new TypeError('The fluid-ounce conversion must be positive.');
    }
    return milliliters / mlPerUsFloz;
  }

  function exactRange(value) {
    return { min_ml: value, max_ml: value };
  }

  function addRanges(left, right) {
    return {
      min_ml: left.min_ml + right.min_ml,
      max_ml: left.max_ml + right.max_ml
    };
  }

  function multiplyRange(volume, multiplier) {
    return {
      min_ml: volume.min_ml * multiplier,
      max_ml: volume.max_ml * multiplier
    };
  }

  function divideRange(volume, divisor) {
    return {
      min_ml: volume.min_ml / divisor,
      max_ml: volume.max_ml / divisor
    };
  }

  function rangesEqual(volume) {
    return Math.abs(volume.max_ml - volume.min_ml) <= EPSILON_ML;
  }

  function validateComponent(component, context, errors) {
    if (!component || typeof component !== 'object') {
      errors.push(context + ' has an invalid component.');
      return;
    }
    if (typeof component.label !== 'string' || !component.label) {
      errors.push(context + ' has an unlabeled component.');
    }
    if (PHASES.indexOf(component.phase) === -1) {
      errors.push(context + ' has an unsupported component phase.');
    }
    if (!component.quantity || ['volume', 'manual'].indexOf(component.quantity.kind) === -1) {
      errors.push(context + ' has an unsupported component quantity.');
      return;
    }
    if (component.quantity.kind === 'volume') {
      if (
        !isFiniteNumber(component.quantity.min_ml) ||
        !isFiniteNumber(component.quantity.max_ml) ||
        component.quantity.min_ml <= 0 ||
        component.quantity.max_ml < component.quantity.min_ml
      ) {
        errors.push(context + ' has an invalid measured volume.');
      }
    }
  }

  function validateDataset(dataset) {
    var errors = [];
    if (!dataset || typeof dataset !== 'object') {
      return ['Calculator data is unavailable.'];
    }
    if (dataset.schema_version !== 2) {
      errors.push('The calculator data version is not supported.');
    }
    if (
      !dataset.calculation ||
      dataset.calculation.canonical_unit !== 'mL' ||
      dataset.calculation.ml_per_us_floz !== ML_PER_US_FLOZ
    ) {
      errors.push('The canonical unit conversion is missing or changed.');
    }
    if (
      !dataset.product ||
      dataset.product.public_name !== 'Cordial Craft Zero-Sugar Lime' ||
      !Array.isArray(dataset.product.bottle_formats_ml) ||
      dataset.product.bottle_formats_ml.join(',') !== '375,750'
    ) {
      errors.push('The reviewed 375 mL and 750 mL planning formats are missing.');
    }
    if (!Array.isArray(dataset.presets) || dataset.presets.length !== 2) {
      errors.push('The two reviewed Cordial Craft ratio presets are missing.');
    } else {
      var expectedPresets = {
        'two-to-one-starting-point': [2, 1],
        'highball-starting-point': [2, 0.75]
      };
      dataset.presets.forEach(function (preset) {
        var expected = preset && expectedPresets[preset.id];
        if (!expected || !Array.isArray(preset.components) || preset.components.length !== 2) {
          errors.push('A ratio preset is invalid.');
          return;
        }
        preset.components.forEach(function (component, index) {
          validateComponent(component, 'Preset ' + preset.id, errors);
          var expectedMl = expected[index] * ML_PER_US_FLOZ;
          if (
            !component.quantity ||
            Math.abs(component.quantity.min_ml - expectedMl) > EPSILON_ML ||
            Math.abs(component.quantity.max_ml - expectedMl) > EPSILON_ML
          ) {
            errors.push('Preset ' + preset.id + ' has changed from its reviewed ratio.');
          }
        });
      });
    }
    if (!Array.isArray(dataset.recipes) || dataset.recipes.length === 0) {
      errors.push('No reviewed recipes are available.');
    } else {
      var handles = Object.create(null);
      dataset.recipes.forEach(function (recipe) {
        if (!recipe || typeof recipe.handle !== 'string' || !recipe.handle) {
          errors.push('A reviewed recipe is missing its handle.');
          return;
        }
        if (handles[recipe.handle]) {
          errors.push('A reviewed recipe handle is duplicated: ' + recipe.handle + '.');
        }
        handles[recipe.handle] = true;
        if (!Array.isArray(recipe.components) || recipe.components.length === 0) {
          errors.push('A reviewed recipe has no components: ' + recipe.handle + '.');
          return;
        }
        recipe.components.forEach(function (component) {
          validateComponent(component, 'Recipe ' + recipe.handle, errors);
        });
      });
      if (Array.isArray(dataset.blocked_recipes)) {
        dataset.blocked_recipes.forEach(function (blocked) {
          if (blocked && handles[blocked.handle]) {
            errors.push('An unresolved recipe was exposed: ' + blocked.handle + '.');
          }
        });
      }
    }
    return errors;
  }

  function findRecipe(dataset, handle) {
    if (!dataset || !Array.isArray(dataset.recipes) || typeof handle !== 'string') {
      return null;
    }
    return dataset.recipes.find(function (recipe) {
      return recipe.handle === handle;
    }) || null;
  }

  function findPreset(dataset, id) {
    if (!dataset || !Array.isArray(dataset.presets) || typeof id !== 'string') {
      return null;
    }
    return dataset.presets.find(function (preset) {
      return preset.id === id;
    }) || null;
  }

  function measuredQuantity(milliliters) {
    return {
      kind: 'volume',
      precision: 'exact',
      min_ml: milliliters,
      max_ml: milliliters,
      source_unit: 'user-selected'
    };
  }

  function parseCustomVolume(rawValue, unit, fieldName, errors) {
    var value = numberValue(rawValue);
    var milliliters = unit === 'metric' ? value : value * ML_PER_US_FLOZ;
    if (!isFiniteNumber(value) || value <= 0) {
      errors[fieldName] = 'Enter a measured amount greater than zero.';
      return null;
    }
    if (milliliters > MAX_COMPONENT_ML) {
      errors[fieldName] = 'Enter no more than 2,000 mL (about 67.6 U.S. fl oz) per serving.';
      return null;
    }
    return milliliters;
  }

  function buildCustomPlan(input, errors) {
    var custom = input.custom || {};
    var unit = custom.unit === 'metric' ? 'metric' : 'us';
    var name = cleanText(custom.name, 80);
    var baseName = cleanText(custom.baseName, 60);
    if (!name) {
      errors.custom_name = 'Enter a name for the custom recipe.';
    }
    if (!baseName) {
      errors.custom_base_name = 'Name the base ingredient.';
    }
    var baseMl = parseCustomVolume(custom.baseAmount, unit, 'custom_base_amount', errors);
    var cordialMl = parseCustomVolume(custom.cordialAmount, unit, 'custom_cordial_amount', errors);
    var components = [];
    if (baseName && baseMl !== null) {
      components.push({
        id: 'custom-base',
        label: baseName,
        source_text: 'User-entered measured base',
        phase: 'prebatch',
        quantity: measuredQuantity(baseMl)
      });
    }
    if (cordialMl !== null) {
      components.push({
        id: 'custom-cordial',
        label: 'Cordial Craft Zero-Sugar Lime',
        source_text: 'User-entered Cordial Craft amount',
        phase: 'prebatch',
        product_key: 'cordial-craft-zero-sugar-lime',
        quantity: measuredQuantity(cordialMl)
      });
    }

    var additional = Array.isArray(custom.additional) ? custom.additional.slice(0, 3) : [];
    additional.forEach(function (item, index) {
      var position = index + 1;
      var itemName = cleanText(item && item.name, 60);
      var hasAmount = item && String(item.amount == null ? '' : item.amount).trim() !== '';
      if (!itemName && !hasAmount) {
        return;
      }
      if (!itemName) {
        errors['additional_name_' + position] = 'Name this ingredient or clear its amount.';
      }
      if (!hasAmount) {
        errors['additional_amount_' + position] = 'Enter this ingredient’s measured amount or clear its name.';
        return;
      }
      var itemMl = parseCustomVolume(
        item.amount,
        unit,
        'additional_amount_' + position,
        errors
      );
      var phase = item.phase === 'add_at_service' ? 'add_at_service' : 'prebatch';
      if (itemName && itemMl !== null) {
        components.push({
          id: 'custom-additional-' + position,
          label: itemName,
          source_text: 'User-entered optional measured ingredient',
          phase: phase,
          quantity: measuredQuantity(itemMl)
        });
      }
    });

    return {
      id: 'custom',
      name: name || 'Custom measured recipe',
      description: 'User-entered measured recipe',
      url: null,
      components: components,
      garnish_instruction: null,
      service_notes: [
        'This custom plan scales only the measured liquids entered here.',
        'Record every garnish, manual technique, allergen, and service instruction on the operating batch sheet.'
      ],
      bottle_count_eligible: true,
      sourceType: 'custom'
    };
  }

  function resolvePlan(dataset, input, errors) {
    var source = typeof input.planSource === 'string' ? input.planSource : '';
    if (source === 'custom') {
      return buildCustomPlan(input, errors);
    }
    if (source.indexOf('preset:') === 0) {
      var preset = findPreset(dataset, source.slice(7));
      if (!preset) {
        errors.plan_source = 'Choose an available starting point or reviewed recipe.';
        return null;
      }
      return Object.assign({}, preset, { url: null, sourceType: 'preset' });
    }
    if (source.indexOf('recipe:') === 0) {
      var recipe = findRecipe(dataset, source.slice(7));
      if (!recipe) {
        errors.plan_source = 'Choose an available starting point or reviewed recipe.';
        return null;
      }
      return Object.assign({}, recipe, { id: recipe.handle, sourceType: 'recipe' });
    }
    errors.plan_source = 'Choose an available starting point, reviewed recipe, or custom build.';
    return null;
  }

  function validateInputs(dataset, input) {
    var errors = {};
    var plan = resolvePlan(dataset, input || {}, errors);
    var calculation = dataset && dataset.calculation ? dataset.calculation : {};
    var servings = numberValue(input && input.servings);
    var overagePercent = numberValue(input && input.overagePercent);
    var dilution = input && input.dilution ? input.dilution : {};
    var dilutionMode = DILUTION_MODES.indexOf(dilution.mode) === -1 ? '' : dilution.mode;
    var inputUnit = input && input.custom && input.custom.unit === 'metric' ? 'metric' : 'us';
    var dilutionValue = 0;

    if (
      !Number.isInteger(servings) ||
      servings < calculation.servings_min ||
      servings > calculation.servings_max
    ) {
      errors.servings =
        'Enter a whole number from ' +
        calculation.servings_min +
        ' to ' +
        calculation.servings_max +
        '.';
    }
    if (
      !isFiniteNumber(overagePercent) ||
      overagePercent < calculation.overage_percent_min ||
      overagePercent > calculation.overage_percent_max
    ) {
      errors.overage =
        'Enter a measured-liquid buffer from ' +
        calculation.overage_percent_min +
        '% to ' +
        calculation.overage_percent_max +
        '%. Use 0% for none.';
    }
    if (!dilutionMode) {
      errors.dilution_mode = 'Select one dilution convention.';
    } else if (dilutionMode === 'measured') {
      dilutionValue = parseCustomVolume(
        dilution.value,
        inputUnit,
        'dilution_measured',
        errors
      );
    } else if (dilutionMode === 'base_percent' || dilutionMode === 'finished_percent') {
      dilutionValue = numberValue(dilution.value);
      if (!isFiniteNumber(dilutionValue) || dilutionValue <= 0 || dilutionValue >= 100) {
        errors.dilution_percent = 'Enter a tested percentage greater than 0 and less than 100.';
      }
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors: errors,
      plan: plan,
      servings: servings,
      overagePercent: overagePercent,
      dilutionMode: dilutionMode,
      dilutionValue: dilutionValue,
      inputUnit: inputUnit
    };
  }

  function scaleComponent(component, multiplier) {
    if (component.quantity.kind === 'manual') {
      return {
        id: component.id,
        label: component.label,
        source_text: component.source_text,
        phase: component.phase,
        product_key: component.product_key || null,
        scaled: {
          kind: 'manual',
          precision: 'source_text',
          per_serving_text: component.quantity.per_serving_text
        }
      };
    }
    return {
      id: component.id,
      label: component.label,
      source_text: component.source_text,
      phase: component.phase,
      product_key: component.product_key || null,
      scaled: {
        kind: 'volume',
        precision: component.quantity.precision,
        min_ml: component.quantity.min_ml * multiplier,
        max_ml: component.quantity.max_ml * multiplier
      }
    };
  }

  function sumMeasured(items) {
    return items.reduce(function (total, item) {
      if (!item.scaled || item.scaled.kind !== 'volume') {
        return total;
      }
      return addRanges(total, item.scaled);
    }, exactRange(0));
  }

  function dilutionRecord(mode, value, base, multiplier) {
    if (mode === 'service') {
      return {
        mode: mode,
        label: 'No advance water',
        explanation: '0 mL is added in advance. Preserve the tested shake, stir, or ice method at service.',
        water: exactRange(0),
        waterPerServing: exactRange(0)
      };
    }
    if (mode === 'measured') {
      var measuredWater = exactRange(value * multiplier);
      return {
        mode: mode,
        label: 'Tested water amount per serving',
        explanation: 'The user-entered measured water amount is multiplied by servings and the optional measured-liquid buffer.',
        water: measuredWater,
        waterPerServing: exactRange(value)
      };
    }
    if (mode === 'base_percent') {
      var baseWater = multiplyRange(base, value / 100);
      return {
        mode: mode,
        label: 'Added water ÷ undiluted pre-batch liquid',
        explanation: 'Added water = undiluted pre-batch liquid × ' + value + '%.',
        water: baseWater,
        waterPerServing: divideRange(baseWater, multiplier)
      };
    }
    var finishedWater = multiplyRange(base, value / (100 - value));
    return {
      mode: mode,
      label: 'Water ÷ finished pre-service batch',
      explanation: 'Added water = undiluted pre-batch liquid × ' + value + ' ÷ (100 − ' + value + ').',
      water: finishedWater,
      waterPerServing: divideRange(finishedWater, multiplier)
    };
  }

  function calculateBottlePlans(result, dataset) {
    var productComponents = [];
    PHASES.forEach(function (phase) {
      result.groups[phase].forEach(function (component) {
        if (component.product_key === dataset.product.key) {
          productComponents.push(component);
        }
      });
    });
    if (
      productComponents.length !== 1 ||
      productComponents[0].scaled.kind !== 'volume' ||
      !rangesEqual(productComponents[0].scaled)
    ) {
      return {
        eligible: false,
        reason: 'Choose one exact Cordial Craft amount before using a bottle plan.'
      };
    }
    if (!result.plan.bottle_count_eligible) {
      return { eligible: false, reason: 'This plan is not cleared for a bottle estimate.' };
    }
    var requiredMl = productComponents[0].scaled.min_ml;
    return {
      eligible: true,
      productName: dataset.product.public_name,
      requiredMl: requiredMl,
      formats: dataset.product.bottle_formats_ml.map(function (sizeMl) {
        var count = Math.ceil(Math.max(0, requiredMl - EPSILON_ML) / sizeMl);
        return {
          sizeMl: sizeMl,
          count: count,
          nominalCapacityMl: count * sizeMl,
          nominalRemainderMl: count * sizeMl - requiredMl
        };
      })
    };
  }

  function calculate(dataset, input) {
    var datasetErrors = validateDataset(dataset);
    if (datasetErrors.length) {
      var dataError = new Error(datasetErrors.join(' '));
      dataError.code = 'INVALID_DATASET';
      throw dataError;
    }
    var validation = validateInputs(dataset, input || {});
    if (!validation.valid) {
      var inputError = new Error('Calculator inputs are invalid.');
      inputError.code = 'INVALID_INPUT';
      inputError.fields = validation.errors;
      throw inputError;
    }

    var multiplier = validation.servings * (1 + validation.overagePercent / 100);
    var groups = {
      prebatch: [],
      add_at_service: [],
      garnish: [],
      manual: []
    };
    validation.plan.components.forEach(function (component) {
      groups[component.phase].push(scaleComponent(component, multiplier));
    });
    if (validation.plan.garnish_instruction) {
      groups.garnish.push({
        id: validation.plan.id + '-garnish-instruction',
        label: 'Service garnish',
        source_text: validation.plan.garnish_instruction,
        phase: 'garnish',
        product_key: null,
        scaled: {
          kind: 'manual',
          precision: 'source_text',
          per_serving_text: validation.plan.garnish_instruction
        }
      });
    }

    var undilutedBatch = sumMeasured(groups.prebatch);
    var dilution = dilutionRecord(
      validation.dilutionMode,
      validation.dilutionValue,
      undilutedBatch,
      multiplier
    );
    if (dilution.water.max_ml > 0) {
      groups.prebatch.push({
        id: validation.plan.id + '-advance-water',
        label: 'Tested advance water',
        source_text: dilution.label,
        phase: 'prebatch',
        product_key: null,
        scaled: {
          kind: 'volume',
          precision: rangesEqual(dilution.water) ? 'exact' : 'range',
          min_ml: dilution.water.min_ml,
          max_ml: dilution.water.max_ml
        }
      });
    }

    var batchTotal = addRanges(undilutedBatch, dilution.water);
    var serviceTotal = sumMeasured(groups.add_at_service);
    var completeTotal = addRanges(batchTotal, serviceTotal);
    if (completeTotal.max_ml > MAX_RESULT_ML) {
      var rangeError = new Error('The calculated plan exceeds the working volume limit.');
      rangeError.code = 'RESULT_TOO_LARGE';
      throw rangeError;
    }

    var result = {
      plan: validation.plan,
      servings: validation.servings,
      overagePercent: validation.overagePercent,
      multiplier: multiplier,
      groups: groups,
      dilution: dilution,
      totals: {
        undilutedBatch: undilutedBatch,
        batch: batchTotal,
        perServingBatch: divideRange(batchTotal, multiplier),
        service: serviceTotal,
        complete: completeTotal
      },
      bottlePlans: null
    };
    result.bottlePlans = calculateBottlePlans(result, dataset);
    return result;
  }

  function numberString(value, maximumFractionDigits) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: maximumFractionDigits
    }).format(value);
  }

  function formatOzNumber(milliliters) {
    var ounces = milliliters / ML_PER_US_FLOZ;
    var places = Math.abs(ounces) >= 100 ? 1 : 2;
    return numberString(roundTo(ounces, places), places);
  }

  function formatMlNumber(milliliters) {
    var places = Math.abs(milliliters) < 10 ? 1 : 0;
    return numberString(roundTo(milliliters, places), places);
  }

  function formatRangePart(volume, formatter, suffix) {
    var minimum = formatter(volume.min_ml);
    if (rangesEqual(volume)) {
      return minimum + suffix;
    }
    return minimum + '–' + formatter(volume.max_ml) + suffix;
  }

  function formatVolumeDual(volume) {
    return (
      formatRangePart(volume, formatOzNumber, ' U.S. fl oz') +
      ' / ' +
      formatRangePart(volume, formatMlNumber, ' mL')
    );
  }

  function dilutionSummary(result) {
    var record = result.dilution;
    if (record.mode === 'service') {
      return record.label + ': ' + record.explanation;
    }
    return (
      record.label +
      ': ' +
      formatVolumeDual(record.waterPerServing) +
      ' advance water per serving; ' +
      formatVolumeDual(record.water) +
      ' for this plan. ' +
      record.explanation
    );
  }

  function uniqueStrings(values) {
    return values.filter(function (value, index) {
      return value && values.indexOf(value) === index;
    });
  }

  function serviceNotes(result) {
    var notes = Array.isArray(result.plan.service_notes) ? result.plan.service_notes.slice() : [];
    notes.push('Actual dilution varies with method, ice, temperature, equipment, and time. Chill and taste one finished serving before making the full batch.');
    if (result.groups.add_at_service.length) {
      notes.push('Keep the listed serving additions separate from the batch and measure them in the order the recipe specifies.');
    }
    notes.push('Keep carbonated ingredients at service unless a recipe-specific, pressure-rated process has been validated.');
    notes.push('Follow applicable food-safety, labeling, holding, allergen, and alcohol-service procedures.');
    return uniqueStrings(notes);
  }

  function plainTextSummary(result) {
    var lines = [
      result.plan.name,
      result.servings + ' target servings' +
        (result.overagePercent > 0
          ? ' + ' + result.overagePercent + '% measured-liquid buffer'
          : ' · no measured-liquid buffer'),
      '',
      'Totals',
      '- Pre-service batch: ' + formatVolumeDual(result.totals.batch),
      '- Per-serving batch pour: ' + formatVolumeDual(result.totals.perServingBatch),
      '- Measured additions at service: ' + formatVolumeDual(result.totals.service),
      '- Complete measured liquid: ' + formatVolumeDual(result.totals.complete),
      '',
      'Dilution convention',
      '- ' + dilutionSummary(result),
      ''
    ];
    PHASES.forEach(function (phase) {
      var components = result.groups[phase];
      if (!components.length) {
        return;
      }
      lines.push(PHASE_LABELS[phase]);
      components.forEach(function (component) {
        var amount = component.scaled.kind === 'volume'
          ? formatVolumeDual(component.scaled)
          : component.scaled.per_serving_text + ' (per serving; keep manual)';
        lines.push('- ' + component.label + ': ' + amount);
      });
      lines.push('');
    });
    lines.push('Cordial Craft bottle planning');
    if (result.bottlePlans.eligible) {
      lines.push('- Required: ' + formatVolumeDual(exactRange(result.bottlePlans.requiredMl)));
      result.bottlePlans.formats.forEach(function (format) {
        lines.push(
          '- ' + format.sizeMl + ' mL format: ' + format.count +
          ' bottle' + (format.count === 1 ? '' : 's') +
          '; nominal volume above plan ' + formatMlNumber(format.nominalRemainderMl) + ' mL'
        );
      });
      lines.push('- Planning comparison only; verify format availability before ordering.');
    } else {
      lines.push('- ' + result.bottlePlans.reason);
    }
    lines.push('');
    lines.push('Tasting and serving notes');
    serviceNotes(result).forEach(function (note) {
      lines.push('- ' + note);
    });
    lines.push('');
    lines.push('Batch service notes');
    lines.push('- Recipe version: ______________________________');
    lines.push('- Checked by / date: __________________________');
    lines.push('- Container and measured fill: _________________');
    lines.push('- Prepared by / time: _________________________');
    lines.push('- Label and storage instructions: ______________');
    lines.push('- First / middle / last pour check: __________');
    if (result.plan.url) {
      lines.push('');
      lines.push('Complete recipe: ' + result.plan.url);
    }
    return lines.join('\n');
  }

  return {
    PHASES: PHASES.slice(),
    PHASE_LABELS: Object.assign({}, PHASE_LABELS),
    DILUTION_MODES: DILUTION_MODES.slice(),
    ML_PER_US_FLOZ: ML_PER_US_FLOZ,
    isFiniteNumber: isFiniteNumber,
    numberValue: numberValue,
    roundTo: roundTo,
    ozToMl: ozToMl,
    mlToOz: mlToOz,
    exactRange: exactRange,
    validateDataset: validateDataset,
    validateInputs: validateInputs,
    findRecipe: findRecipe,
    findPreset: findPreset,
    calculate: calculate,
    calculateBottlePlans: calculateBottlePlans,
    formatVolumeDual: formatVolumeDual,
    formatMlNumber: formatMlNumber,
    dilutionSummary: dilutionSummary,
    serviceNotes: serviceNotes,
    plainTextSummary: plainTextSummary
  };
});
